/**
 * UserProfile domain types.
 */

// ─── Standard Wrapper ─────────────────────────────────────────────────────────

/** Generic success/failure envelope returned by most mutation endpoints. */
export interface UserProfileActionResponse {
  statusCode: number;
  success: boolean;
  message: string;
  /** Present on UserRelation and Invite responses. */
  isFollowing?: boolean;
  /** Returned by Follow Agent / Switch Agent — the resolved conversation ID. */
  conversationId?: number | null;
}

export interface ReportUserRequest {
  reportedUserGuid: string;
  violationTypes: number[];
  otherDescription?: string;
}

// ─── Upsert ───────────────────────────────────────────────────────────────────

/** Request body for profile creation/upsert. */
export interface UserProfileUpsertRequest {
  userId: string;
  /** Integer foreign key for gender. Pending lookup endpoint. */
  genderId?: number;
  /** Integer foreign key for country. Pending lookup endpoint. */
  countryId?: number;
  /** Height in centimetres. */
  heightCm?: number;
  /** Weight in kilograms. */
  weightKg?: number;
  /** ISO 8601 date-time string (e.g. "2022-08-28T03:43:09.338Z"). */
  dateOfBirth?: string;
  /**
   * Profile picture URL or base64 string.
   * TODO: Confirm with backend whether this is a URL, base64, or multipart upload.
   */
  profilePicture?: string;
  aboutMe?: string;
  bio?: string;
}

// ─── Profile Details ──────────────────────────────────────────────────────────

/** AI Buddy agent that the user is currently following. */
export interface FollowedAiAgent {
  agentId?: number | null;
  agentGuid?: string | null;
  userName?: string | null;
  profilePicture?: string | null;
  /** Resolved Robi/WlbHelpAgent conversation ID — cached after first lookup. */
  conversationId?: number | null;
}

/** Response body for fetching the user profile. */
export interface UserProfileResponse {
  statusCode?: number;
  success?: boolean;
  message?: string;
  isAccessible?: boolean;
  userId?: number;
  userGuid?: string;
  firstName?: string;
  lastName?: string;
  userName?: string;
  email?: string;
  gender?: string;
  dateOfBirth?: string;
  age?: number;
  country?: string;
  height?: number;
  heightScaleUnit?: number;
  weight?: number;
  weightScaleUnit?: number;
  profilePicture?: string;
  /** Profile-picture aliases returned by older/runtime profile endpoints. */
  profileImage?: string | null;
  profileImageUrl?: string | null;
  profilePictureUrl?: string | null;
  userPhoto?: string | null;
  aboutMe?: string;
  bio?: string;
  numberOfPosts?: number;
  followersCount?: number;
  followingCount?: number;
  buddiesCount?: number;

  // Legacy fields
  displayName?: string;
  genderId?: number;
  countryId?: number;
  heightCm?: number;
  weightKg?: number;
  postCount?: number;
  following?: number;
  followers?: number;
  buddies?: number;
  totalPoints?: number;
  weeklyPoints?: number;
  currentWeight?: number;
  scaleUnitWeightName?: string;
  scaleUnitHeightName?: string;
  startWeight?: number;
  goalWeight?: number;
  countryPrivacy?: number;
  weightPrivacy?: number;
  dateOfBirthPrivacy?: number;
  profileViewPrivacy?: number;
  heightPrivacy?: number;
  bioPrivacy?: number;
  aboutMePrivacy?: number;
  genderPrivacy?: number;
  profilePicturePrivacy?: number;
  isBuddy?: boolean;
  invited?: boolean;
  isRequested?: boolean;
  isAccepted?: boolean;
  /** AI Buddy currently followed by this user (null when none selected). */
  followedAiAgent?: FollowedAiAgent | null;
  isFollowing?: boolean;
  isFollower?: boolean;
  status?: string | null;
}

/** Request body for updating the user profile. */
export interface UserProfileUpdateRequest {
  firstName?: string;
  lastName?: string;
  bio?: string;
  aboutMe?: string;
  /** Legacy upsert-era field — integer gender FK. */
  genderId?: number;
  /** Legacy upsert-era field — integer country FK. */
  countryId?: number;
  /** Height in centimetres. */
  heightCm?: number;
  heightScaleUnit?: number;
  /** PUT /me field — current weight in kilograms (or user's unit). */
  weightKg?: number;
  weightScaleUnit?: number;
  /** User's starting weight (in their selected unit). */
  startWeight?: number;
  /** User's starting weight in Kg. */
  startWeightKg?: number;
  /** Scale unit for startWeight (1 = kg, 2 = lbs). */
  startWeightScaleUnit?: number;
  /** User's goal weight (in their selected unit). */
  goalWeight?: number;
  /** User's goal weight in Kg. */
  goalWeightKg?: number;
  /** Scale unit for goalWeight (1 = kg, 2 = lbs). */
  goalWeightScaleUnit?: number;
  profileViewPrivacy?: number;
  weightPrivacy?: number;
  heightPrivacy?: number;
  countryPrivacy?: number;
  bioPrivacy?: number;
  aboutMePrivacy?: number;
  dateOfBirthPrivacy?: number;
  genderPrivacy?: number;
  profilePicturePrivacy?: number;
  /** ISO 8601 date string (YYYY-MM-DD). */
  dateOfBirth?: string;
  /** URL of the profile picture. */
  profilePicture?: string;
  /** Web only: the picked File/Blob, which browsers require in FormData. */
  profilePictureBlob?: Blob | null;
}

/** Response envelope for UpsertProfilePrivacySettings. */
export interface UpdateProfilePrivacySettingsResponse {
  isSuccess?: boolean;
  message?: string;
  data?: {
    profile?: UserProfileResponse;
  };
}

// ─── Chart Data ───────────────────────────────────────────────────────────────

/** Request body for fetching weight chart data. */
export interface ChartDataRequest {
  /** ISO 8601 date-time — start of the range. */
  from?: string;
  /** ISO 8601 date-time — end of the range. */
  to?: string;
  /** Aggregation period (e.g., Daily, Weekly, Monthly). */
  periodicalType?: string;
  /** The metric to chart (e.g., Weight). */
  chartName?: string;
}

export interface ChartPoint {
  x: number;
  y: number;
  xLabel?: string;
}

export interface ChartData {
  chartData?: ChartPoint[];
  minValue: number;
  maxValue: number;
}

export interface ChartDataResponse {
  statusCode: number;
  success: boolean;
  message?: string;
  data?: ChartData;
}

// ─── Update Measurement ───────────────────────────────────────────────────────

/** Request body for updating a user measurement. */
export interface UpdateMeasurementRequest {
  /** Weight value in the unit indicated by weightScaleUnit (backend converts). */
  currentWeight: number;
  /** Optional height in the unit indicated by heightScaleUnit. */
  currentHeightCm?: number;
  /** 1 = kg, 2 = lbs */
  weightScaleUnit?: number;
  /** 3 = cm, 4 = ft */
  heightScaleUnit?: number;
}

// ─── User Relation ────────────────────────────────────────────────────────────

/** Valid action values for the UserRelation endpoint. */
export type UserRelationAction =
  | 'Follow'
  | 'UnFollow'
  | 'Block'
  | 'UnBlock'
  | 'BuddyInvite'
  | 'Agent'          // AI Buddy follow / switch
  | string; // allow future values without breaking TS

/** Request body for performing a user relation action. */
export interface UserRelationRequest {
  relatedUserId: number | string;
  action: UserRelationAction;
}

// ─── Invite ───────────────────────────────────────────────────────────────────

/** Request body for accepting/declining buddy invites. */
export interface InviteRequest {
  senderId: number;
  status: number;
}

// ─── Get User Connections ─────────────────────────────────────────────────────

/** Request body for fetching followers, following, or buddies. */
export interface GetUserConnectionsRequest {
  profileUserId: number;
  /** The connection type to fetch. */
  relation: string;
  startIndex: number;
  pageSize: number;
  timezoneOffset: number;
  searchText?: string;
}

export interface UserConnectionItem {
  userId: number;
  profileUserGuid: string;
  userName: string;
  firstName: string;
  lastName?: string;
  userPhoto: string;
  currentWeight: number;
  scaleUnitWeightName: string;
  currentHeight: number;
  scaleUnitHeightName: string;
  isBuddy: boolean;
  invited: boolean;
  following: number;
  followers: number;
  buddies: number;
  gender: string;
  country: string;
  joinedAt: string;
  userGuid:string
}

export interface GetUserConnectionsResponse {
  statusCode: number;
  message: string;
  success: boolean;
  users: UserConnectionItem[];
}

// ─── Get Blocked Users ────────────────────────────────────────────────────────

/** Query params for retrieving the blocked-users list. */
export interface GetBlockedUsersRequest {
  /** Pass "block" to retrieve the blocked-users list. */
  relation: 'block';
}

/**
 * A single blocked user.
 * The backend reuses the same user shape as GetUserConnections.
 */
export interface BlockedUserItem {
  userId: number;
  userName: string;
  firstName: string;
  userPhoto: string;
  /** ISO 8601 date-time when the block was applied (if returned by backend). */
  joinedAt?: string;
  gender?: string;
  country?: string;
}

export interface GetBlockedUsersResponse {
  statusCode: number;
  message: string;
  success: boolean;
  users: BlockedUserItem[];
}

// ─── Buddy Search ─────────────────────────────────────────────────────────────

/** Request body for POST /api/v1/UserProfile/BuddySearch. All fields are optional filters. */
export interface BuddySearchRequest {
  /** Search term for user name. Empty string if not provided. */
  userName?: string;
  /** Minimum age filter. */
  ageFrom?: number;
  /** Maximum age filter. */
  ageTo?: number;
  /** Gender ID (numeric). 1 = Male, 2 = Female — omit for all. */
  gender?: number;
  /** Country ID(s) or codes (numeric/string array). Omit for all countries. */
  country?: number[];
  /** Current weight lower bound in kg. */
  currentWeightFrom?: number;
  /** Current weight upper bound in kg. */
  currentWeightTo?: number;
  /** Weight scale unit ID — 1 = kg, 2 = lbs. */
  currentWeightScaleUnit?: number;
  /** How much weight the user wants to lose, in kg. */
  wantToLose?: number | null;
  /** Weight-to-lose scale unit ID — 1 = kg, 2 = lbs. */
  weightToLoseScaleUnit?: number;
}

/** A single buddy returned by the BuddySearch API. */
export interface BuddySearchResultUser {
  userId: number;
  userGuid: string;
  /** Legacy alias for `userGuid`; mobile call sites still reference it. */
  profileUserGuid?: string | null;
  userName: string;
  userPhoto: string;
  weightToLose: number;
  weightToLoseUnit: string;
  matchPercentage: number;
  isRequested?: boolean;
  status?: string;
  isBuddy?: boolean;
  invited?: boolean;
  isAccepted?: boolean;
  completedAge?: number;
  location?: string;
  lostWeight?: string;
  description?:string;
}

/** Response envelope for POST /api/v1/UserProfile/BuddySearch. */
export interface BuddySearchResponse {
  statusCode: number;
  message: string;
  success: boolean;
  buddies: BuddySearchResultUser[];
  totalRecords?: number;
  totalPages?: number;
  hasMore?: boolean;
}

export interface AccountSettingsResponse {
  userId: number;
  userGuid: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  profilePicture?: string | null;
  profileImage?: string | null;
  weightScaleUnit?: number | null; // 1 = kg, 2 = lbs
  profileViewPrivacy: number;      // 1 = Public, 2 = Buddies only, 3 = Private
  weightPrivacy: number;            // 1 = Public, 2 = Buddies only, 3 = Only me
  isOnlineVisible: boolean;
  notificationsEnabled: boolean;
  videosStartWithSound: boolean;
  allowSharing: boolean;
}

export interface AccountSettingsResponseApiResponse {
  isSuccess: boolean;
  message?: string | null;
  errors?: string[] | null;
  data?: AccountSettingsResponse | null;
}

export interface AccountSettingsUpdateRequest {
  weightScaleUnit?: number | null;
  profileViewPrivacy?: number | null;
  weightPrivacy?: number | null;
  isOnlineVisible?: boolean | null;
  notificationsEnabled?: boolean | null;
  videosStartWithSound?: boolean | null;
  allowSharing?: boolean | null;
}

export interface LeaderBoardListRequest {
  startIndex: number;
  pageSize: number;
}

export interface LeaderBoardItem {
  rank: number;
  userId: number;
  userGuid: string;
  userName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  profilePicture?: string | null;
  totalPoints: number;
  currentLevelName?: string | null;
  currentLevelIcon?: string | null;
}

export interface LeaderBoardCurrentUser {
  rank: number;
  totalPoints: number;
  currentLevelName?: string | null;
  currentLevelIcon?: string | null;
}

export interface LeaderBoardListResponse {
  statusCode: number;
  message?: string | null;
  success: boolean;
  totalCount: number;
  currentUser?: LeaderBoardCurrentUser | null;
  leaderBoard?: LeaderBoardItem[] | null;
}

export interface AwardsSummary {
  totalPoints: number;
  currentLevelName?: string | null;
  currentLevelIcon?: string | null;
  nextLevelName?: string | null;
  nextLevelMinPoints?: number | null;
  pointsToNextLevel: number;
  progressPercent: number;
}

export interface AwardLevelItem {
  levelName?: string | null;
  minPoints: number;
  maxPoints?: number | null;
  iconKey?: string | null;
  isCurrent: boolean;
}

export interface EarnRuleItem {
  ruleName?: string | null;
  ruleDescription?: string | null;
  points: number;
  ruleFor?: string | null;
}

export interface AwardsResponse {
  statusCode: number;
  message?: string | null;
  success: boolean;
  summary?: AwardsSummary | null;
  levels?: AwardLevelItem[] | null;
  earnRules?: EarnRuleItem[] | null;
}

export const REPORT_REASON_OPTIONS: ReportReason[] = [
  { id: 1, labelKey: 'profile.report.options.spam' },
  { id: 2, labelKey: 'profile.report.options.dontLikeIt' },
  { id: 3, labelKey: 'profile.report.options.bullying' },
  { id: 4, labelKey: 'profile.report.options.suicide' },
  { id: 5, labelKey: 'profile.report.options.violence' },
  { id: 6, labelKey: 'profile.report.options.restrictedItems' },
  { id: 7, labelKey: 'profile.report.options.nudity' },
  { id: 8, labelKey: 'profile.report.options.scam' },
  { id: 9, labelKey: 'profile.report.options.falseInfo' },
  { id: 10, labelKey: 'profile.report.options.other' },
];

export const OTHER_REPORT_REASON_ID = 10;

export interface ReportReason {
  id: number;
  /** i18n key under profile.report.options.* */
  labelKey: string;
}

export interface ReportUserPayload {
  reportedUserId: number;
  reportReasonIds: number[];
  otherReason?: string;
}

export interface GetUserBuddyPreferencesResponse {
  statusCode: number;
  message: string;
  success: boolean;

  userBuddyPreferenceId: number;
  userId: number;
  userGuid: string;

  mainGoalId: number;
  targetWeightLossKg: number;

  commitmentLevelId: number;
  preferredBuddyTypeId: number;
  communicationStyleId: number;
  checkInFrequencyId: number;
  preferredBuddyGenderId: number;

  preferredBuddyAgeMin: number;
  preferredBuddyAgeMax: number;

  ratingMotivationToWeightLoss: number;
  ratingConsistentHealthyHabit: number;
  ratingAccountability: number;
  ratingProgressSharingComfortLevel: number;
  ratingCheckInFrequency: number;
  ratingEmotionalSupportImportance: number;
}

export interface BuddyMatchingRequest {
  buddyTypeFilter: string;
}

export interface BuddyMatchingItem {
  buddyId?: string;
  name?: string;
  profilePictureUrl?: string;
  gender?: string;
  age?: number;
  about?: string;
  description?: string;
  score?: number;
  userGuid?: string;
  isRequested?: boolean;
  status?: string;
  motivationStyle?: string;
}

export interface BuddyMatchingResponse {
  topCount?: number;
  matches?: BuddyMatchingItem[];
  totalRecords?: number;
  totalPages?: number;
  hasMore?: boolean;
}